import { Debouncer } from "@tanstack/react-pacer";
import { create } from "zustand";
import type { TimelineScale } from "./components/projects/ProjectsTimelineGrid";
import type { ScopedThreadRef } from "@cmmd-forge/contracts";
import {
  INITIAL_PROJECT_LEAD_COMPANION_STATE,
  openProjectLeadCompanionState,
  type ProjectLeadCompanionDock,
  type ProjectLeadCompanionPosition,
  type ProjectLeadCompanionState,
} from "./projectLeadCompanionState";
import {
  clearThreadLinkedPr,
  clearThreadUi,
  markThreadUnread,
  markThreadVisited,
  sanitizePersistedThreadSnooze,
  setThreadActiveOrderKey,
  setThreadChangedFilesExpanded,
  setThreadLinkedPr,
  setThreadPinned,
  setThreadPinOrderKey,
  setThreadSnooze,
  toggleThreadPinned,
} from "./uiStateThreadOverlay";

const PERSISTED_STATE_KEY = "forge:ui-state:v1";
const LEGACY_PERSISTED_STATE_KEYS = [
  "t3code:ui-state:v1",
  "t3code:renderer-state:v8",
  "t3code:renderer-state:v7",
  "t3code:renderer-state:v6",
  "t3code:renderer-state:v5",
  "t3code:renderer-state:v4",
  "t3code:renderer-state:v3",
  "codething:renderer-state:v4",
  "codething:renderer-state:v3",
  "codething:renderer-state:v2",
  "codething:renderer-state:v1",
] as const;

interface PersistedUiState {
  expandedProjectCwds?: string[];
  projectOrderCwds?: string[];
  projectGroupOrderKeys?: string[];
  threadChangedFilesExpandedById?: Record<string, Record<string, boolean>>;
  pinnedThreadKeys?: string[];
  threadLinkedPrById?: Record<string, string>;
  pinOrderKeyById?: Record<string, string>;
  activeOrderKeyById?: Record<string, string>;
  threadSnoozeById?: Record<string, SidebarThreadSnooze>;
  timelineWorkColumnWidth?: number;
  timelineScale?: TimelineScale;
}

export const TIMELINE_SCALE_DEFAULT: TimelineScale = "months";
const TIMELINE_SCALES: ReadonlySet<TimelineScale> = new Set(["weeks", "months", "quarters"]);

export function sanitizeTimelineScale(value: unknown): TimelineScale {
  return typeof value === "string" && TIMELINE_SCALES.has(value as TimelineScale)
    ? (value as TimelineScale)
    : TIMELINE_SCALE_DEFAULT;
}

// Projects Timeline: the left "Work" column is user-resizable. Bounds keep it
// usable (never narrower than a truncated title) without letting it swallow the
// timeline grid.
export const TIMELINE_WORK_COLUMN_DEFAULT_PX = 360;
export const TIMELINE_WORK_COLUMN_MIN_PX = 200;
export const TIMELINE_WORK_COLUMN_MAX_PX = 560;

export function clampTimelineWorkColumnWidth(px: number): number {
  if (!Number.isFinite(px)) return TIMELINE_WORK_COLUMN_DEFAULT_PX;
  return Math.round(
    Math.min(TIMELINE_WORK_COLUMN_MAX_PX, Math.max(TIMELINE_WORK_COLUMN_MIN_PX, px)),
  );
}

export interface UiProjectState {
  projectExpandedById: Record<string, boolean>;
  projectOrder: string[];
  projectGroupOrder: string[];
  /**
   * The project the user is currently working in, or was last working in this
   * session. `null` before any project route is visited.
   *
   * Exists because `/new` needs to answer "which project was I in?" and nothing
   * else could (CMMD-2266). `projectOrder` is the persisted DRAG arrangement, and
   * `threadLastVisitedAtById` — despite the name — is an unread-clearing record:
   * its writer only fires for a thread with newly completed activity, so
   * re-opening an already-read thread records nothing and `markThreadUnread`
   * writes synthetic timestamps into it. Neither can answer the question, so this
   * field does, written from the active route.
   *
   * Held as a ref rather than a pre-formatted key on purpose: consumers key
   * projects differently (`scopedProjectKey` adds a versioned prefix for ids
   * containing a colon; the `/new` picker uses a plain `env:id` template), so
   * storing one spelling would silently fail to match the other.
   *
   * Deliberately NOT persisted: a stale cross-session value is worse than
   * falling back to sidebar order, and a reload of a `/new` URL carries its own
   * `?project=` param anyway.
   */
  lastActiveProject: { readonly environmentId: string; readonly projectId: string } | null;
}

/** Snooze stamps for the sidebar v2 overlay. Wake classification is derived
 * from `snoozedUntil` at read time, so only the two write-time stamps persist. */
export interface SidebarThreadSnooze {
  readonly snoozedAt: string;
  readonly snoozedUntil: string;
}

export interface UiThreadState {
  threadLastVisitedAtById: Record<string, string>;
  threadChangedFilesExpandedById: Record<string, Record<string, boolean>>;
  pinnedThreadById: Record<string, boolean>;
  threadLinkedPrById: Record<string, string>;
  /**
   * Sidebar v2 overlay: fractional pin order keys (base-26 midpoints, shared
   * math with upstream via @t3tools/client-runtime/state/thread-sort), keyed
   * by scoped thread key. Upstream persists these on the thread record
   * server-side; forge keeps them client-local next to the pin map, like v1.
   */
  pinOrderKeyById: Record<string, string>;
  /** Sidebar v2 overlay: manual active-list order keys, same persistence model. */
  activeOrderKeyById: Record<string, string>;
  /** Sidebar v2 overlay: snooze stamps by scoped thread key. */
  threadSnoozeById: Record<string, SidebarThreadSnooze>;
}

export type SidebarDensity = "compact" | "comfortable";

export interface UiState extends UiProjectState, UiThreadState {
  workspaceDensityById: Record<string, SidebarDensity>;
  timelineWorkColumnWidth: number;
  timelineScale: TimelineScale;
  projectLeadCompanion: ProjectLeadCompanionState;
}

export interface SyncProjectInput {
  key: string;
  cwd: string;
}

export interface SyncThreadInput {
  key: string;
  seedVisitedAt?: string | undefined;
}

const initialState: UiState = {
  projectExpandedById: {},
  projectOrder: [],
  projectGroupOrder: [],
  lastActiveProject: null,
  threadLastVisitedAtById: {},
  threadChangedFilesExpandedById: {},
  pinnedThreadById: {},
  threadLinkedPrById: {},
  pinOrderKeyById: {},
  activeOrderKeyById: {},
  threadSnoozeById: {},
  workspaceDensityById: {},
  timelineWorkColumnWidth: TIMELINE_WORK_COLUMN_DEFAULT_PX,
  timelineScale: TIMELINE_SCALE_DEFAULT,
  projectLeadCompanion: INITIAL_PROJECT_LEAD_COMPANION_STATE,
};

const persistedExpandedProjectCwds = new Set<string>();
const persistedProjectOrderCwds: string[] = [];
const currentProjectCwdById = new Map<string, string>();
let legacyKeysCleanedUp = false;

/** Read and sanitize stored UI preferences, hydrating legacy project state and falling back to defaults on read errors. */
function readPersistedState(): UiState {
  if (typeof window === "undefined") {
    return initialState;
  }
  try {
    const raw = window.localStorage.getItem(PERSISTED_STATE_KEY);
    if (!raw) {
      for (const legacyKey of LEGACY_PERSISTED_STATE_KEYS) {
        const legacyRaw = window.localStorage.getItem(legacyKey);
        if (!legacyRaw) {
          continue;
        }
        hydratePersistedProjectState(JSON.parse(legacyRaw) as PersistedUiState);
        return initialState;
      }
      return initialState;
    }
    const parsed = JSON.parse(raw) as PersistedUiState;
    hydratePersistedProjectState(parsed);
    return {
      ...initialState,
      projectGroupOrder: sanitizePersistedStringList(parsed.projectGroupOrderKeys),
      threadChangedFilesExpandedById: sanitizePersistedThreadChangedFilesExpanded(
        parsed.threadChangedFilesExpandedById,
      ),
      pinnedThreadById: sanitizePersistedPinnedThreads(parsed.pinnedThreadKeys),
      threadLinkedPrById: sanitizePersistedThreadLinkedPrs(parsed.threadLinkedPrById),
      pinOrderKeyById: sanitizePersistedStringRecord(parsed.pinOrderKeyById),
      activeOrderKeyById: sanitizePersistedStringRecord(parsed.activeOrderKeyById),
      threadSnoozeById: sanitizePersistedThreadSnooze(parsed.threadSnoozeById),
      timelineWorkColumnWidth: clampTimelineWorkColumnWidth(
        parsed.timelineWorkColumnWidth ?? TIMELINE_WORK_COLUMN_DEFAULT_PX,
      ),
      timelineScale: sanitizeTimelineScale(parsed.timelineScale),
    };
  } catch {
    return initialState;
  }
}

function sanitizePersistedStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return [
    ...new Set(
      value.filter((entry): entry is string => typeof entry === "string" && entry.length > 0),
    ),
  ];
}

function sanitizePersistedPinnedThreads(
  value: PersistedUiState["pinnedThreadKeys"],
): Record<string, boolean> {
  if (!Array.isArray(value)) {
    return {};
  }
  const pinned: Record<string, boolean> = {};
  for (const threadKey of value) {
    if (typeof threadKey === "string" && threadKey.length > 0) {
      pinned[threadKey] = true;
    }
  }
  return pinned;
}

function sanitizePersistedThreadLinkedPrs(
  value: PersistedUiState["threadLinkedPrById"],
): Record<string, string> {
  if (!value || typeof value !== "object") {
    return {};
  }
  const linkedPrByThreadId: Record<string, string> = {};
  for (const [threadId, prUrl] of Object.entries(value)) {
    if (typeof threadId !== "string" || threadId.length === 0) continue;
    if (typeof prUrl !== "string" || prUrl.trim().length === 0) continue;
    linkedPrByThreadId[threadId] = prUrl;
  }
  return linkedPrByThreadId;
}

/** Retain only nonempty keys with nonempty string values from persisted order records. */
function sanitizePersistedStringRecord(
  value: Record<string, string> | undefined,
): Record<string, string> {
  if (!value || typeof value !== "object") {
    return {};
  }
  const sanitized: Record<string, string> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (
      typeof key === "string" &&
      key.length > 0 &&
      typeof entry === "string" &&
      entry.length > 0
    ) {
      sanitized[key] = entry;
    }
  }
  return sanitized;
}

/** Restore per-thread collapsed-turn overrides, dropping threads with no valid overrides. */
function sanitizePersistedThreadChangedFilesExpanded(
  value: PersistedUiState["threadChangedFilesExpandedById"],
): Record<string, Record<string, boolean>> {
  if (!value || typeof value !== "object") {
    return {};
  }

  const nextState: Record<string, Record<string, boolean>> = {};
  for (const [threadId, turns] of Object.entries(value)) {
    const nextTurns = sanitizeCollapsedTurns(turns);
    if (Object.keys(nextTurns).length > 0) {
      nextState[threadId] = nextTurns;
    }
  }

  return nextState;
}

/** Retain only nonempty turn IDs explicitly marked collapsed; expanded turns use the default state. */
function sanitizeCollapsedTurns(
  turns: Record<string, boolean> | undefined,
): Record<string, boolean> {
  const nextTurns: Record<string, boolean> = {};
  if (!turns || typeof turns !== "object") {
    return nextTurns;
  }
  for (const [turnId, expanded] of Object.entries(turns)) {
    if (turnId && typeof expanded === "boolean" && expanded === false) {
      nextTurns[turnId] = false;
    }
  }
  return nextTurns;
}

/** Collect nonempty persisted project directories for expansion restoration when projects arrive. */
function hydrateExpandedProjectCwds(expanded: readonly unknown[]): void {
  for (const cwd of expanded) {
    if (typeof cwd === "string" && cwd.length > 0) {
      persistedExpandedProjectCwds.add(cwd);
    }
  }
}

/** Restore persisted project-directory order, ignoring empty values and duplicate directories. */
function hydrateProjectOrderCwds(order: readonly unknown[]): void {
  for (const cwd of order) {
    if (typeof cwd === "string" && cwd.length > 0 && !persistedProjectOrderCwds.includes(cwd)) {
      persistedProjectOrderCwds.push(cwd);
    }
  }
}

/** Replace the pending project expansion and ordering caches from persisted directory lists. */
function hydratePersistedProjectState(parsed: PersistedUiState): void {
  persistedExpandedProjectCwds.clear();
  persistedProjectOrderCwds.length = 0;
  hydrateExpandedProjectCwds(parsed.expandedProjectCwds ?? []);
  hydrateProjectOrderCwds(parsed.projectOrderCwds ?? []);
}

/** Save durable UI preferences using project directories and scoped thread keys; tolerate unavailable browser storage. */
function persistState(state: UiState): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    const expandedProjectCwds = Object.entries(state.projectExpandedById)
      .filter(([, expanded]) => expanded)
      .flatMap(([projectId]) => {
        const cwd = currentProjectCwdById.get(projectId);
        return cwd ? [cwd] : [];
      });
    const projectOrderCwds = state.projectOrder.flatMap((projectId) => {
      const cwd = currentProjectCwdById.get(projectId);
      return cwd ? [cwd] : [];
    });
    const threadChangedFilesExpandedById = Object.fromEntries(
      Object.entries(state.threadChangedFilesExpandedById).flatMap(([threadId, turns]) => {
        const nextTurns = Object.fromEntries(
          Object.entries(turns).filter(([, expanded]) => expanded === false),
        );
        return Object.keys(nextTurns).length > 0 ? [[threadId, nextTurns]] : [];
      }),
    );
    window.localStorage.setItem(
      PERSISTED_STATE_KEY,
      JSON.stringify({
        expandedProjectCwds,
        projectOrderCwds,
        projectGroupOrderKeys: state.projectGroupOrder,
        threadChangedFilesExpandedById,
        pinnedThreadKeys: Object.keys(state.pinnedThreadById).filter(
          (threadKey) => state.pinnedThreadById[threadKey],
        ),
        threadLinkedPrById: state.threadLinkedPrById,
        pinOrderKeyById: state.pinOrderKeyById,
        activeOrderKeyById: state.activeOrderKeyById,
        threadSnoozeById: state.threadSnoozeById,
        timelineWorkColumnWidth: state.timelineWorkColumnWidth,
        timelineScale: state.timelineScale,
      } satisfies PersistedUiState),
    );
    if (!legacyKeysCleanedUp) {
      legacyKeysCleanedUp = true;
      for (const legacyKey of LEGACY_PERSISTED_STATE_KEYS) {
        window.localStorage.removeItem(legacyKey);
      }
    }
  } catch {
    // Ignore quota/storage errors to avoid breaking chat UX.
  }
}

const debouncedPersistState = new Debouncer(persistState, { wait: 500 });

function recordsEqual<T>(left: Record<string, T>, right: Record<string, T>): boolean {
  const leftEntries = Object.entries(left);
  const rightEntries = Object.entries(right);
  if (leftEntries.length !== rightEntries.length) {
    return false;
  }
  for (const [key, value] of leftEntries) {
    if (right[key] !== value) {
      return false;
    }
  }
  return true;
}

function projectOrdersEqual(left: readonly string[], right: readonly string[]): boolean {
  return (
    left.length === right.length && left.every((projectId, index) => projectId === right[index])
  );
}

function nestedBooleanRecordsEqual(
  left: Record<string, Record<string, boolean>>,
  right: Record<string, Record<string, boolean>>,
): boolean {
  const leftEntries = Object.entries(left);
  const rightEntries = Object.entries(right);
  if (leftEntries.length !== rightEntries.length) {
    return false;
  }
  for (const [key, value] of leftEntries) {
    if (!(key in right) || !recordsEqual(value, right[key]!)) {
      return false;
    }
  }
  return true;
}

export function syncProjects(state: UiState, projects: readonly SyncProjectInput[]): UiState {
  const previousProjectCwdById = new Map(currentProjectCwdById);
  const previousProjectIdByCwd = new Map(
    [...previousProjectCwdById.entries()].map(([projectId, cwd]) => [cwd, projectId] as const),
  );
  currentProjectCwdById.clear();
  for (const project of projects) {
    currentProjectCwdById.set(project.key, project.cwd);
  }
  const cwdMappingChanged =
    previousProjectCwdById.size !== currentProjectCwdById.size ||
    projects.some((project) => previousProjectCwdById.get(project.key) !== project.cwd);

  const nextExpandedById: Record<string, boolean> = {};
  const previousExpandedById = state.projectExpandedById;
  const persistedOrderByCwd = new Map(
    persistedProjectOrderCwds.map((cwd, index) => [cwd, index] as const),
  );
  const mappedProjects = projects.map((project, index) => {
    const previousProjectIdForCwd = previousProjectIdByCwd.get(project.cwd);
    const expanded =
      previousExpandedById[project.key] ??
      (previousProjectIdForCwd ? previousExpandedById[previousProjectIdForCwd] : undefined) ??
      (persistedExpandedProjectCwds.size > 0
        ? persistedExpandedProjectCwds.has(project.cwd)
        : true);
    nextExpandedById[project.key] = expanded;
    return {
      id: project.key,
      cwd: project.cwd,
      incomingIndex: index,
    };
  });

  const nextProjectOrder =
    state.projectOrder.length > 0
      ? (() => {
          const nextProjectIdByCwd = new Map(
            mappedProjects.map((project) => [project.cwd, project.id] as const),
          );
          const usedProjectIds = new Set<string>();
          const orderedProjectIds: string[] = [];

          for (const projectId of state.projectOrder) {
            const matchedProjectId =
              (projectId in nextExpandedById ? projectId : undefined) ??
              (() => {
                const previousCwd = previousProjectCwdById.get(projectId);
                return previousCwd ? nextProjectIdByCwd.get(previousCwd) : undefined;
              })();
            if (!matchedProjectId || usedProjectIds.has(matchedProjectId)) {
              continue;
            }
            usedProjectIds.add(matchedProjectId);
            orderedProjectIds.push(matchedProjectId);
          }

          for (const project of mappedProjects) {
            if (usedProjectIds.has(project.id)) {
              continue;
            }
            orderedProjectIds.push(project.id);
          }

          return orderedProjectIds;
        })()
      : mappedProjects
          .map((project) => ({
            id: project.id,
            incomingIndex: project.incomingIndex,
            orderIndex:
              persistedOrderByCwd.get(project.cwd) ??
              persistedProjectOrderCwds.length + project.incomingIndex,
          }))
          .toSorted((left, right) => {
            const byOrder = left.orderIndex - right.orderIndex;
            if (byOrder !== 0) {
              return byOrder;
            }
            return left.incomingIndex - right.incomingIndex;
          })
          .map((project) => project.id);

  if (
    recordsEqual(state.projectExpandedById, nextExpandedById) &&
    projectOrdersEqual(state.projectOrder, nextProjectOrder) &&
    !cwdMappingChanged
  ) {
    return state;
  }

  return {
    ...state,
    projectExpandedById: nextExpandedById,
    projectOrder: nextProjectOrder,
  };
}

/** Prune UI state for missing thread keys and seed first-visit timestamps, preserving state identity when unchanged. */
export function syncThreads(state: UiState, threads: readonly SyncThreadInput[]): UiState {
  const retainedThreadIds = new Set(threads.map((thread) => thread.key));
  const nextThreadLastVisitedAtById = Object.fromEntries(
    Object.entries(state.threadLastVisitedAtById).filter(([threadId]) =>
      retainedThreadIds.has(threadId),
    ),
  );
  for (const thread of threads) {
    if (
      nextThreadLastVisitedAtById[thread.key] === undefined &&
      thread.seedVisitedAt !== undefined &&
      thread.seedVisitedAt.length > 0
    ) {
      nextThreadLastVisitedAtById[thread.key] = thread.seedVisitedAt;
    }
  }
  const nextThreadChangedFilesExpandedById = Object.fromEntries(
    Object.entries(state.threadChangedFilesExpandedById).filter(([threadId]) =>
      retainedThreadIds.has(threadId),
    ),
  );
  const nextPinnedThreadById = Object.fromEntries(
    Object.entries(state.pinnedThreadById).filter(
      ([threadId, pinned]) => retainedThreadIds.has(threadId) && pinned,
    ),
  );
  const nextThreadLinkedPrById = Object.fromEntries(
    Object.entries(state.threadLinkedPrById).filter(([threadId]) =>
      retainedThreadIds.has(threadId),
    ),
  );
  const nextPinOrderKeyById = Object.fromEntries(
    Object.entries(state.pinOrderKeyById).filter(([threadId]) => retainedThreadIds.has(threadId)),
  );
  const nextActiveOrderKeyById = Object.fromEntries(
    Object.entries(state.activeOrderKeyById).filter(([threadId]) =>
      retainedThreadIds.has(threadId),
    ),
  );
  const nextThreadSnoozeById = Object.fromEntries(
    Object.entries(state.threadSnoozeById).filter(([threadId]) => retainedThreadIds.has(threadId)),
  );
  if (
    recordsEqual(state.threadLastVisitedAtById, nextThreadLastVisitedAtById) &&
    nestedBooleanRecordsEqual(
      state.threadChangedFilesExpandedById,
      nextThreadChangedFilesExpandedById,
    ) &&
    recordsEqual(state.pinnedThreadById, nextPinnedThreadById) &&
    recordsEqual(state.threadLinkedPrById, nextThreadLinkedPrById) &&
    recordsEqual(state.pinOrderKeyById, nextPinOrderKeyById) &&
    recordsEqual(state.activeOrderKeyById, nextActiveOrderKeyById) &&
    recordsEqual(state.threadSnoozeById, nextThreadSnoozeById)
  ) {
    return state;
  }
  return {
    ...state,
    threadLastVisitedAtById: nextThreadLastVisitedAtById,
    threadChangedFilesExpandedById: nextThreadChangedFilesExpandedById,
    pinnedThreadById: nextPinnedThreadById,
    threadLinkedPrById: nextThreadLinkedPrById,
    pinOrderKeyById: nextPinOrderKeyById,
    activeOrderKeyById: nextActiveOrderKeyById,
    threadSnoozeById: nextThreadSnoozeById,
  };
}

/**
 * Record which project the user is working in, for `/new` to default to.
 *
 * Compared field by field rather than by reference: the caller derives this from
 * route state on every render, so an equal-but-fresh object must not restart the
 * store's subscriber cascade.
 */
export function setLastActiveProject(
  state: UiState,
  project: { readonly environmentId: string; readonly projectId: string } | null,
): UiState {
  if (
    state.lastActiveProject?.environmentId === project?.environmentId &&
    state.lastActiveProject?.projectId === project?.projectId
  ) {
    return state;
  }
  return { ...state, lastActiveProject: project };
}

export function setWorkspaceDensity(
  state: UiState,
  workspaceId: string,
  density: SidebarDensity,
): UiState {
  if (state.workspaceDensityById[workspaceId] === density) {
    return state;
  }
  return {
    ...state,
    workspaceDensityById: {
      ...state.workspaceDensityById,
      [workspaceId]: density,
    },
  };
}

export function toggleProject(state: UiState, projectId: string): UiState {
  const expanded = state.projectExpandedById[projectId] ?? true;
  return {
    ...state,
    projectExpandedById: {
      ...state.projectExpandedById,
      [projectId]: !expanded,
    },
  };
}

export function setProjectExpanded(state: UiState, projectId: string, expanded: boolean): UiState {
  if ((state.projectExpandedById[projectId] ?? true) === expanded) {
    return state;
  }
  return {
    ...state,
    projectExpandedById: {
      ...state.projectExpandedById,
      [projectId]: expanded,
    },
  };
}

export function reorderProjects(
  state: UiState,
  draggedProjectIds: readonly string[],
  targetProjectIds: readonly string[],
): UiState {
  if (draggedProjectIds.length === 0) {
    return state;
  }
  const draggedSet = new Set(draggedProjectIds);
  const targetSet = new Set(targetProjectIds);
  if (draggedProjectIds.every((id) => targetSet.has(id))) {
    return state;
  }

  const originalTargetIndex = state.projectOrder.findIndex((id) => targetSet.has(id));
  if (originalTargetIndex < 0) {
    return state;
  }

  const projectOrder = [...state.projectOrder];

  const removed: string[] = [];
  let draggedBeforeTarget = 0;
  for (let i = projectOrder.length - 1; i >= 0; i--) {
    if (draggedSet.has(projectOrder[i]!)) {
      removed.unshift(projectOrder.splice(i, 1)[0]!);
      if (i < originalTargetIndex) {
        draggedBeforeTarget++;
      }
    }
  }
  if (removed.length === 0) {
    return state;
  }

  const insertIndex = originalTargetIndex - Math.max(0, draggedBeforeTarget - 1);
  projectOrder.splice(insertIndex, 0, ...removed);
  return {
    ...state,
    projectOrder,
  };
}

export function syncProjectGroups(state: UiState, groupKeys: readonly string[]): UiState {
  const incomingKeys = [...new Set(groupKeys)];
  const incomingKeySet = new Set(incomingKeys);
  const nextOrder = state.projectGroupOrder.filter((key) => incomingKeySet.has(key));
  const retainedKeys = new Set(nextOrder);
  for (const key of incomingKeys) {
    if (!retainedKeys.has(key)) nextOrder.push(key);
  }
  return projectOrdersEqual(state.projectGroupOrder, nextOrder)
    ? state
    : { ...state, projectGroupOrder: nextOrder };
}

export function reorderProjectGroups(state: UiState, activeKey: string, overKey: string): UiState {
  if (activeKey === overKey) return state;
  const activeIndex = state.projectGroupOrder.indexOf(activeKey);
  const overIndex = state.projectGroupOrder.indexOf(overKey);
  if (activeIndex < 0 || overIndex < 0) return state;
  const nextOrder = [...state.projectGroupOrder];
  const [active] = nextOrder.splice(activeIndex, 1);
  if (active === undefined) return state;
  nextOrder.splice(overIndex, 0, active);
  return { ...state, projectGroupOrder: nextOrder };
}

interface UiStateStore extends UiState {
  syncProjects: (projects: readonly SyncProjectInput[]) => void;
  syncProjectGroups: (groupKeys: readonly string[]) => void;
  syncThreads: (threads: readonly SyncThreadInput[]) => void;
  markThreadVisited: (threadId: string, visitedAt?: string) => void;
  markThreadUnread: (threadId: string, latestTurnCompletedAt: string | null | undefined) => void;
  clearThreadUi: (threadId: string) => void;
  setThreadPinned: (threadId: string, pinned: boolean) => void;
  toggleThreadPinned: (threadId: string) => void;
  setThreadPinOrderKey: (threadId: string, orderKey: string | null) => void;
  setThreadActiveOrderKey: (threadId: string, orderKey: string | null) => void;
  setThreadSnooze: (threadId: string, snooze: SidebarThreadSnooze | null) => void;
  setThreadLinkedPr: (threadId: string, prUrl: string) => void;
  clearThreadLinkedPr: (threadId: string) => void;
  setThreadChangedFilesExpanded: (threadId: string, turnId: string, expanded: boolean) => void;
  setWorkspaceDensity: (workspaceId: string, density: SidebarDensity) => void;
  toggleProject: (projectId: string) => void;
  setProjectExpanded: (projectId: string, expanded: boolean) => void;
  reorderProjects: (
    draggedProjectIds: readonly string[],
    targetProjectIds: readonly string[],
  ) => void;
  reorderProjectGroups: (activeKey: string, overKey: string) => void;
  setLastActiveProject: (
    project: { readonly environmentId: string; readonly projectId: string } | null,
  ) => void;
  setTimelineWorkColumnWidth: (px: number) => void;
  setTimelineScale: (scale: TimelineScale) => void;
  openProjectLeadCompanion: (threadRef: ScopedThreadRef) => void;
  closeProjectLeadCompanion: () => void;
  setProjectLeadCompanionMinimized: (minimized: boolean) => void;
  setProjectLeadCompanionDock: (dock: ProjectLeadCompanionDock) => void;
  setProjectLeadCompanionPosition: (position: ProjectLeadCompanionPosition) => void;
}

export const useUiStateStore = create<UiStateStore>((set) => ({
  ...readPersistedState(),
  syncProjects: (projects) => set((state) => syncProjects(state, projects)),
  syncProjectGroups: (groupKeys) => set((state) => syncProjectGroups(state, groupKeys)),
  syncThreads: (threads) => set((state) => syncThreads(state, threads)),
  markThreadVisited: (threadId, visitedAt) =>
    set((state) => markThreadVisited(state, threadId, visitedAt)),
  markThreadUnread: (threadId, latestTurnCompletedAt) =>
    set((state) => markThreadUnread(state, threadId, latestTurnCompletedAt)),
  clearThreadUi: (threadId) => set((state) => clearThreadUi(state, threadId)),
  setThreadPinned: (threadId, pinned) => set((state) => setThreadPinned(state, threadId, pinned)),
  toggleThreadPinned: (threadId) => set((state) => toggleThreadPinned(state, threadId)),
  setThreadPinOrderKey: (threadId, orderKey) =>
    set((state) => setThreadPinOrderKey(state, threadId, orderKey)),
  setThreadActiveOrderKey: (threadId, orderKey) =>
    set((state) => setThreadActiveOrderKey(state, threadId, orderKey)),
  setThreadSnooze: (threadId, snooze) => set((state) => setThreadSnooze(state, threadId, snooze)),
  setThreadLinkedPr: (threadId, prUrl) => set((state) => setThreadLinkedPr(state, threadId, prUrl)),
  clearThreadLinkedPr: (threadId) => set((state) => clearThreadLinkedPr(state, threadId)),
  setThreadChangedFilesExpanded: (threadId, turnId, expanded) =>
    set((state) => setThreadChangedFilesExpanded(state, threadId, turnId, expanded)),
  setWorkspaceDensity: (workspaceId, density) =>
    set((state) => setWorkspaceDensity(state, workspaceId, density)),
  toggleProject: (projectId) => set((state) => toggleProject(state, projectId)),
  setProjectExpanded: (projectId, expanded) =>
    set((state) => setProjectExpanded(state, projectId, expanded)),
  reorderProjects: (draggedProjectIds, targetProjectIds) =>
    set((state) => reorderProjects(state, draggedProjectIds, targetProjectIds)),
  reorderProjectGroups: (activeKey, overKey) =>
    set((state) => reorderProjectGroups(state, activeKey, overKey)),
  setLastActiveProject: (project) => set((state) => setLastActiveProject(state, project)),
  setTimelineWorkColumnWidth: (px) =>
    set((state) => ({ ...state, timelineWorkColumnWidth: clampTimelineWorkColumnWidth(px) })),
  setTimelineScale: (scale) => set((state) => ({ ...state, timelineScale: scale })),
  openProjectLeadCompanion: (threadRef) =>
    set((state) => ({
      ...state,
      projectLeadCompanion: openProjectLeadCompanionState(state.projectLeadCompanion, threadRef),
    })),
  closeProjectLeadCompanion: () =>
    set((state) => ({
      ...state,
      projectLeadCompanion: { ...state.projectLeadCompanion, open: false },
    })),
  setProjectLeadCompanionMinimized: (minimized) =>
    set((state) => ({
      ...state,
      projectLeadCompanion: { ...state.projectLeadCompanion, minimized },
    })),
  setProjectLeadCompanionDock: (dock) =>
    set((state) => ({
      ...state,
      projectLeadCompanion: { ...state.projectLeadCompanion, dock },
    })),
  setProjectLeadCompanionPosition: (position) =>
    set((state) => ({
      ...state,
      projectLeadCompanion: { ...state.projectLeadCompanion, position },
    })),
}));

export function openProjectLeadCompanion(threadRef: ScopedThreadRef): void {
  useUiStateStore.getState().openProjectLeadCompanion(threadRef);
}

useUiStateStore.subscribe((state) => debouncedPersistState.maybeExecute(state));

if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
  window.addEventListener("beforeunload", () => {
    debouncedPersistState.flush();
  });
}
